import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import { RootStoreContext } from "src/stores/RootStore";
import { observer } from "mobx-react";
import SplitPane from "react-split-pane";
import { DEFAULT_AGENDAITEM_PANE_WIDTH } from "src/stores/UIStore";
import "./AgendaItemSplitView.css";
import { AgendaItemDetails } from "src/screens/assemblies/assembly/agenda/details/AgendaItemDetails";
import { AgendaItemsSidebar } from "src/screens/assemblies/assembly/agenda/sidebar/AgendaItemsSidebar";
import { FreeTextView } from "src/screens/assemblies/assembly/agenda/freetext/FreeTextView";

const AgendaItemSplitViewBase = () => {
    const { uiStore, assemblyStore } = useContext(RootStoreContext);

    useEffect(() => {
        uiStore.setInvoicePaneWidth(DEFAULT_AGENDAITEM_PANE_WIDTH);

        // Needs to only run one time
        // eslint-disable-next-line
    }, []);

    // refs for each pane wrapper (to measure height)
    const pane1Ref = useRef<HTMLDivElement>(null);
    const pane2Ref = useRef<HTMLDivElement>(null);

    // state to track the taller pane’s height
    const [maxChildHeight, setMaxChildHeight] = useState<number>(0);

    // ref for the SplitPane itself
    const splitPaneRef = useRef<any>(null);

    // Whenever pane1 or pane2 changes size, recompute the max height.
    useEffect(() => {
        const ro1 = new ResizeObserver((entries) => {
            for (let entry of entries) {
                if (entry.target === pane1Ref.current) {
                    const h1 = entry.contentRect.height;
                    const h2 = pane2Ref.current?.getBoundingClientRect().height ?? 0;
                    setMaxChildHeight(Math.max(h1, h2));
                }
            }
        });
        const ro2 = new ResizeObserver((entries) => {
            for (let entry of entries) {
                if (entry.target === pane2Ref.current) {
                    const h2 = entry.contentRect.height;
                    const h1 = pane1Ref.current?.getBoundingClientRect().height ?? 0;
                    setMaxChildHeight(Math.max(h1, h2));
                }
            }
        });

        if (pane1Ref.current) ro1.observe(pane1Ref.current);
        if (pane2Ref.current) ro2.observe(pane2Ref.current);

        return () => {
            ro1.disconnect();
            ro2.disconnect();
        };
    }, []);

    const onDragStarted = useCallback(() => {
        uiStore.setIsResizeObserverDisabled(false);
    }, [uiStore]);

    const onDividerChange = useCallback(
        (newSize: number) => {
            uiStore.setInvoicePaneWidth(newSize);
        },
        [uiStore]
    );

    const onResizerDoubleClick = useCallback(() => {
        uiStore.resetInvoicePaneWidth();
    }, [uiStore]);

    return (
        <div id="split-pane-container" style={{ width: "100%", height: maxChildHeight }}>
            <SplitPane
                ref={splitPaneRef}
                className="agendaitem-details-split-pane"
                defaultSize={DEFAULT_AGENDAITEM_PANE_WIDTH}
                minSize={100}
                size={uiStore.invoicePaneWidth}
                onDragStarted={onDragStarted}
                onChange={onDividerChange}
                onResizerDoubleClick={onResizerDoubleClick}
            >
                {/* Wrap each child in its own <div> with ref */}
                <div ref={pane1Ref}>
                    <AgendaItemsSidebar />
                </div>
                <div ref={pane2Ref}>
                    {assemblyStore.isFreetextHeaderExpanded || assemblyStore.isFreetextFooterExpanded ? (
                        <FreeTextView />
                    ) : (
                        <AgendaItemDetails />
                    )}
                </div>
            </SplitPane>
        </div>
    );
};

export const AgendaItemSplitView = Sentry.withProfiler(observer(AgendaItemSplitViewBase));
